import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  resendSignUpCode,
  getCurrentUser,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword,
  autoSignIn,
  SignUpOutput,
  SignInOutput,
} from 'aws-amplify/auth';
import { getErrorMessage } from '@/lib/get-amplify-error-messages';
import { useAuthStore } from '@/store/use-auth-store';
import { apiGetUserProfile } from '@/services/user-service';

export type UserInfo = {
  userId: string;
  email?: string;
  phoneNumber: string;
  isAuthenticated: boolean;
  accessToken?: string;
};

interface AuthHook {
  signUpUser: (email: string, phoneNumber: string, password: string) => Promise<SignUpOutput>;
  confirmSignUpUser: (email: string, code: string) => Promise<{ isSignUpComplete: boolean; nextStep?: string }>;
  autoSignInUser: () => Promise<void>;
  signInUser: (email: string, password: string, redirectUrl?: string) => Promise<SignInOutput>;
  signOutUser: (redirectUrl?: string) => Promise<string>;
  sendEmailVerificationCode: (email: string) => Promise<void>;
  resendEmailSignUpCode: (email: string) => Promise<void>;
  getCurrentUserInfo: () => Promise<UserInfo | null>;
  sendForgotPasswordCode: (email: string) => Promise<void>;
  confirmResetPasswordUser: (email: string, code: string, newPassword: string) => Promise<void>;
}

export default function useAuth(): AuthHook {
  async function signUpUser(email: string, phoneNumber: string, password: string): Promise<SignUpOutput> {
    try {
      return await signUp({
        username: email,
        password,
        options: {
          userAttributes: { email, phone_number: phoneNumber },
          autoSignIn: true
        }
      });
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error('Error signing up:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  async function sendEmailVerificationCode(email: string): Promise<void> {
    try {
      await resendSignUpCode({ username: email });
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error('Error sending verification code:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  async function resendEmailSignUpCode(email: string): Promise<void> {
    try {
      console.log('Verification code sent to:', email);
      await resendSignUpCode({ username: email });
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error('Error sending verification code:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  async function confirmSignUpUser(
    email: string,
    code: string
  ): Promise<{ isSignUpComplete: boolean; nextStep?: any }> {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username: String(email),
        confirmationCode: String(code)
      });
      // if (isSignUpComplete && nextStep.signUpStep === "COMPLETE_AUTO_SIGN_IN") {
      //   return await autoSignIn()
      // }
      // console.log('Sign up confirmation result:', isSignUpComplete, nextStep);
      return { isSignUpComplete, nextStep };
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error('Error confirming sign up:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  async function autoSignInUser(): Promise<void> {
    try {
      await autoSignIn()
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error('Error auto signing in:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  async function signInUser(email: string, password: string, redirectUrl = '/dashboard'): Promise<SignInOutput> {
    let redirectLink = redirectUrl;
    try {
      const { isSignedIn, nextStep } = await signIn({ username: email, password });
      // console.log("Cognito isSigned nextStep :", isSignedIn, nextStep)
      if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        await resendSignUpCode({ username: String(email) });
        redirectLink = '/auth/confirm-signup';
      } else if (nextStep.signInStep === 'RESET_PASSWORD') {
        redirectLink = '/auth/reset-password';
      } else if (nextStep.signInStep === 'DONE') {
        redirectLink = '/'
      }
      return { isSignedIn, nextStep };
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      // if (error.code === 'UserNotConfirmedException') {
      //   await resendSignUpCode({ username: String(email) });
      //   return '/auth/confirm-signup';
      // }
      console.error('Error signing in:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  async function signOutUser(redirectUrl = '/auth/login'): Promise<string> {
    try {
      await signOut();
      useAuthStore.getState().setUser(null);
      return redirectUrl;
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error('Error signing out:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  async function getCurrentUserInfo() {
    try {
      let user;
      try {
        user = await getCurrentUser(); // may throw UserUnAuthenticatedException
      } catch (error) {
        console.warn('No signed-in user found:', error);
        useAuthStore.getState().setUser(null);
        return null;
      }
      const userProfile = await apiGetUserProfile();
      // console.log("user profile : ", userProfile)
      const session = await fetchAuthSession();
      const userInfo = {
        userId: user.userId,
        email: userProfile.data.email,
        phoneNumber: userProfile.data.phoneNumber,
        isAuthenticated: !!session.tokens?.accessToken,
        accessToken: session.tokens?.accessToken?.toString()
      };
      useAuthStore.getState().setUser(userInfo);
      return userInfo;
    } catch (error) {
      useAuthStore.getState().setUser(null);
      console.error('Failed to fetch authenticated user:', error);
      return null;
    }
  }

  async function sendForgotPasswordCode(email: string): Promise<void> {
    try {
      await resetPassword({ username: email });
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error('Error initiating password reset:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  async function confirmResetPasswordUser(email: string, code: string, newPassword: string): Promise<void> {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      console.error('Error resetting password:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  return {
    signUpUser,
    confirmSignUpUser,
    autoSignInUser,
    signInUser,
    signOutUser,
    sendEmailVerificationCode,
    resendEmailSignUpCode,
    getCurrentUserInfo,
    sendForgotPasswordCode,
    confirmResetPasswordUser
  };
}
